import Popover from "../../popover/popover";
import {MutableRefObject, useEffect, useRef, useState} from "react";
import Image from "next/image";
import { KeenSliderInstance, KeenSliderPlugin, useKeenSlider } from "keen-slider/react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks";
import { conditions, imageFormatChecking } from "../../../../../../helpers";
import { ConditionName, zIndex } from "../../../../../../interfaces";
import CloseIcon from "../../../icon-components/closeIcon";
import { setTrailerPopover } from "../../../../../../redux/slices/popoversSlice";
import ArrowLeftTrailerIcon from "../../../icon-components/arrowLeftTrailer";
import ArrowRightTrailerIcon from "../../../icon-components/arrowRightTrailer";
import IMAGES from "../../../../../../images/importImages";
import { shallowEqual } from 'react-redux';
import dynamic from "next/dynamic";

const ArrowTopTrailerIcon = dynamic(() => import("../../../icon-components/arrowTopTrailer"));
const ArrowBottomTrailerIcon = dynamic(() => import("../../../icon-components/arrowBottomTrailer"));


const TrailerPopover = () => {
    const dispatch = useAppDispatch();
    useAppSelector(({ popovers: { trailerPopover } }) => ({ trailerPopover }), shallowEqual);
    const { details, trailer } = useAppSelector(({ videoData: { details, trailer, isComplexDesktop, isComplexPortrait } }) => ({ details, trailer, isComplexDesktop, isComplexPortrait }), shallowEqual);

    const [selectedSlide, setSelectedSlide] = useState<number>(0);
    const [trailerMenu, setTrailerMenu] = useState<boolean>(true);
    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        initial: 0,
    });
    const selectedSlideRef = useRef<number>(selectedSlide);
    const [thumbnailRef] = useKeenSlider<HTMLDivElement>(
        {
            initial: 0,
            slides: {
                perView: conditions(ConditionName.isComplexPortrait) ? 3 : 10,
                spacing: 10,
            },
        },
        [Slider(instanceRef)]
    )

    function Slider(
        mainRef: MutableRefObject<KeenSliderInstance | null>
    ): KeenSliderPlugin {
        return (slider) => {
            function removeActive() {
                slider.slides.forEach((slide) => {
                    slide.classList.remove("active")
                })
            }

            function addActive(idx: number) {
                if (idx !== selectedSlideRef.current) (document.getElementById(String(`trailer-video-${selectedSlideRef.current}`)) as HTMLVideoElement)?.pause()
                setSelectedSlide(idx);
                slider.slides[idx].classList.add("active")
            }

            function addClickEvents() {
                slider.slides.forEach((slide, idx) => {
                    slide.addEventListener("click", () => {
                        if (mainRef.current) {
                            mainRef.current.moveToIdx(idx)
                        }
                    })
                })
            }

            slider.on("created", () => {
                if (!mainRef.current) return
                addActive(slider.track.details.rel)
                addClickEvents()
                mainRef.current.on("animationStarted", (main) => {
                    removeActive()
                    const next = main.animator.targetIdx || 0
                    addActive(main.track.absToRel(next))
                    slider.moveToIdx(Math.min(slider.track.details.maxIdx, next))
                })
            })
        }
    }

    useEffect(() => {
        selectedSlideRef.current = selectedSlide;
    }, [selectedSlide])

    const trailerPopoverHeader = () => {
        return (
            <div
                className="pl-flex pl-items-center pl-justify-between pl-py-[19px] pl-px-4 pl-w-full">
                <CloseIcon
                    className={`pl-cursor-pointer dark:pl-stroke-dark-5 pl-stroke-z-gray-700`}
                    onClick={() => dispatch(setTrailerPopover(false))}
                />
                <p className="pl-text-[18px] pl-text-black dark:pl-text-white pl-font-bold">{`${selectedSlide + 1}/${trailer.length}`}</p>
            </div>
        )
    }

    return (
        <Popover
            type="modal"
            status={conditions(ConditionName.showTrailerPopover)}
            onClose={() => {}}
            isHeader={true}
            isFullScreen={true}
            customHeader={trailerPopoverHeader()}
            classNames={`!pl-h-full !pl-w-full !pl-bg-white dark:!pl-bg-dark-1`}
            parentClassNames={"!pl-w-full !pl-h-full"}
        >
            <div className="pl-flex pl-justify-center pl-relative pl-w-full pl-h-full">
                <div
                    className={`pl-absolute ${conditions(ConditionName.isComplexDesktop) ? "pl-top-0 pl-mt-8" : "pl-mb-[105px]"} pl-w-full pl-justify-between pl-flex pl-self-center`}>
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (selectedSlide !== trailer.length - 1) instanceRef.current?.next();
                        }}
                        style={{zIndex: zIndex.trailer}}
                        className={`${conditions(ConditionName.isComplexPortrait) && "pl-absolute pl-right-0 pl-bg-opacity-50 !pl-bg-black/[0.7]"} ${(selectedSlide + 1 === trailer.length) && "!pl-opacity-50 !pl-cursor-default"} pl-flex pl-items-center pl-bg-transparent pl-self-center pl-items-center pl-w-8 pl-h-8 pl-cursor-pointer`}>
                        <ArrowLeftTrailerIcon className={`${conditions(ConditionName.isComplexPortrait) ? "pl-fill-gray-highLight" : "pl-fill-z-gray-700 dark:pl-fill-gray-highLight"}`}/>
                    </div>
                    <div
                        className={`${conditions(ConditionName.isComplexDesktop) ? "pl-w-[790px] pl-top-0" : "pl-w-full"}`}>
                        <div ref={sliderRef} className={`keen-slider`}>
                            {
                                trailer.map((item: any, index: number) => {
                                        return (
                                            <div key={index} className="keen-slider__slide number-slide1">
                                                {
                                                    imageFormatChecking(item.file_name) ? (
                                                        <div className="!pl-w-full !pl-h-full">
                                                            <Image layout={"fill"}
                                                                   src={item.url}/>
                                                        </div>
                                                    ) : (
                                                        <video id={`trailer-video-${index}`} className={"pl-w-full pl-h-full"}
                                                               controls>
                                                            <source src={item.url} type="video/mp4"/>
                                                        </video>
                                                    )
                                                }
                                            </div>
                                        )
                                    }
                                )
                            }
                        </div>
                    </div>
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (selectedSlide !== 0) instanceRef.current?.prev();
                        }}
                        style={{zIndex: zIndex.trailer}}
                        className={`${conditions(ConditionName.isComplexPortrait) && "pl-absolute pl-left-0 pl-bg-opacity-50 !pl-bg-black/[0.7]"} ${(selectedSlide === 0) && "!pl-opacity-50 !pl-cursor-default"} pl-flex pl-items-center pl-bg-transparent pl-self-center pl-items-center pl-w-8 pl-h-8 pl-cursor-pointer`}>
                        <ArrowRightTrailerIcon className={`${conditions(ConditionName.isComplexPortrait) ? "pl-fill-gray-highLight" : "pl-fill-z-gray-700 dark:pl-fill-gray-highLight"}`}/>
                    </div>
                </div>
                <div
                    style={{
                        transform: `translateY(${trailerMenu ? 0 : conditions(ConditionName.isComplexPortrait) ? 100 : document.getElementById("menuTrailer")?.getBoundingClientRect().height}px)`
                    }}
                    className={`pl-transition-transform pl-ease-linear pl-duration-300 keen-slider thumbnail !pl-mt-0 pl-flex pl-flex-col pl-self-end`}>
                    <div className={`pl-flex pl-items-center pl-w-full`}>
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setTrailerMenu(!trailerMenu);
                            }}
                            className={`${conditions(ConditionName.isComplexDesktop) && "pl-mr-10"} pl-flex pl-self-start pl-text-[14px] dark:pl-text-secondary-50 pl-text-secondary-800 pl-font-[700] pl-w-[60px] pl-h-[48px] pl-bg-gray-highLight dark:pl-bg-black pl-items-center pl-justify-center pl-rounded-t-[4px] pl-cursor-pointer`}>
                            {
                                trailerMenu ?
                                    <ArrowTopTrailerIcon className="pl-fill-gray-600 dark:pl-fill-gray-highLight"/> :
                                    <ArrowBottomTrailerIcon className="pl-fill-gray-600 dark:pl-fill-gray-highLight"/>
                            }
                        </div>
                        <div
                            className="pl-flex pl-self-center pl-justify-center pl-w-full pl-text-[14px] dark:pl-text-secondary-50 pl-text-secondary-800 pl-font-[700] pl-ml-[30px] pl-truncate">{details.title}</div>
                    </div>
                    <div
                        className={`pl-bg-gray-highLight dark:pl-bg-black pl-px-4 pl-py-2 pl-w-full`} id={"menuTrailer"}>
                        <div ref={thumbnailRef} className="keen-slider thumbnail pl-py-2">
                            {
                                trailer.map((item: any, index: number) => {
                                    return (
                                        <div
                                            key={index}
                                            className={`${conditions(ConditionName.isComplexPortrait) && "!pl-w-[112px] !pl-h-[63px]"} keen-slider__slide !pl-rounded-[4px] number-slide1 !pl-aspect-video`}>
                                            <>
                                                {
                                                    !imageFormatChecking(item.file_name) &&
                                                    <div
                                                        style={{zIndex: zIndex.trailer}}
                                                        className="pl-absolute pl-flex">
                                                        <Image
                                                            src={IMAGES.bigPlay}
                                                            width={conditions(ConditionName.isComplexPortrait) ? 21 : 40}
                                                            height={conditions(ConditionName.isComplexPortrait) ? 21 : 40}
                                                            className={`pl-big-pause-icon`}
                                                        />
                                                    </div>
                                                }
                                                <div className={"pl-w-full pl-h-full"}>
                                                    <Image
                                                        src={!imageFormatChecking(item.file_name) ? item.banner : item.url}
                                                        layout={"fill"}
                                                    />
                                                </div>
                                            </>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    </div>
                </div>
            </div>
        </Popover>
    )
}

export default TrailerPopover;